import i18next from 'i18next';
import Backend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';
import { load } from 'js-yaml';
import bundledPl from './locales/bundled-pl.json';
import { isAdminSurface } from './utils/adminSurface';
import { isCrawlerUserAgent } from './utils/crawlerUserAgent';
import { SUPPORTED_LANGUAGES, isSupportedLanguage, getLandingLanguage, rememberExplicitLanguage, resolveLanguagePreferences, type SupportedLanguageCode } from './utils/supportedLanguages';

/**
 * i18next configuration for multi-language support.
 *
 * Supported languages: pl, uk, en, fr, bg, vi (Vietnamese).
 * Default language: pl (Polish)
 *
 * Language can be changed at runtime via i18next.changeLanguage(lang).
 * User preference is stored in Firestore Users/{uid}.preferredLanguage
 */
const parseYaml = (data: string) => {
  const parsed = load(data);
  if (parsed && typeof parsed === 'object') {
    return parsed;
  }
  return {};
};

const isSupported = isSupportedLanguage;

const getEnvLanguage = (): string | undefined =>
  process.env.REACT_APP_LANGUAGE || process.env.NEXT_PUBLIC_LANGUAGE;

/**
 * The language the server renders: the deployment's own language (`fr` on
 * laloire) or Polish. It never reads the request, the browser or the Node
 * runtime (Node 21+ has a global `navigator` whose language is the machine
 * locale), so the server HTML and the client's first render always agree.
 */
export const getDomainLanguage = (): SupportedLanguageCode => {
  const envLanguage = getEnvLanguage();
  return isSupported(envLanguage) ? envLanguage : 'pl';
};

/** The visitor's own language: what the UI shows once the page is running. */
export const getInitialLanguage = (): SupportedLanguageCode => {
  const envLanguage = getEnvLanguage();
  // The admin cockpit (/mnb/apka and the Android shell around it) is a
  // Polish-only surface and must not follow a device locale that happens to be
  // English: it has two users and both are Polish. Checked before storage and
  // before the browser language, so the app never renders a single English
  // frame - Polish is bundled in the JS, the other languages are fetched.
  if (isAdminSurface()) {
    return 'pl';
  }

  if (typeof window !== 'undefined') {
    const landingLanguage = getLandingLanguage(window.location.pathname);
    if (landingLanguage) return landingLanguage;
    const linkedLanguage = new URLSearchParams(window.location.search).get('lang');
    if (isSupported(linkedLanguage)) return linkedLanguage;
  }

  if (envLanguage === 'fr') {
    return 'fr';
  }

  // Googlebot, bingbot and most other renderers run with
  // navigator.language = en-US. Following it got prawmi.pl indexed in English
  // (prawmi-9o7bk), so crawlers get the domain default, like the server H1
  // (app/(public)/utils/seoLanguage.ts), and never the browser language.
  if (isCrawlerUserAgent()) {
    return getDomainLanguage();
  }

  if (typeof window !== 'undefined') {
    try {
      return resolveLanguagePreferences({
        explicit: localStorage.getItem('explicitLanguagePreference'),
        account: localStorage.getItem('accountLanguagePreference'),
        cached: localStorage.getItem('preferredLanguage'),
        source: localStorage.getItem('languagePreferenceSource'),
        fallback: getDomainLanguage(),
      });
    } catch (_) {
      // Ignore storage access issues
    }
  }
  return getDomainLanguage();
};

/** Static loading text for use before i18next translations load */
const LOADING_TEXTS: Record<string, string> = {
  pl: 'Ładowanie...',
  en: 'Loading...',
  fr: 'Chargement...',
  bg: 'Зареждане...',
  uk: 'Завантаження...',
  vi: 'Đang tải...',
};

/**
 * In the active language, not the visitor's: the loading placeholder is
 * server-rendered and hydrated, so it must say what the server said.
 */
export const getStaticLoadingText = (): string =>
  LOADING_TEXTS[(i18next.language || '').split('-')[0]] ?? LOADING_TEXTS.pl;

const isBrowser = typeof window !== 'undefined';

/**
 * The first render, on the server and in the browser alike, uses the server's
 * language: any other choice made the client's hydration render differ from
 * the server HTML (React #418/#425 on / for every non-Polish visitor). The
 * visitor's own language follows right after hydration, see
 * applyPreferredLanguage().
 */
const serverLanguage = getDomainLanguage();
const preferredLanguage = isBrowser ? getInitialLanguage() : serverLanguage;
if (isBrowser && (getLandingLanguage(window.location.pathname) ||
  isSupportedLanguage(new URLSearchParams(window.location.search).get('lang')))) {
  rememberExplicitLanguage(preferredLanguage);
}

i18next.use(Backend).use(initReactI18next).init({
  ns: ['teams', 'referrals', 'common', 'dashboard', 'dev', 'instruction', 'settings', 'start', 'watches', 'matters', 'narzedzia', 'adminApp', 'personalizedSlot', 'ksiegowi', 'welcome', 'pismo', 'docTemplates',
    // Eagerly load gift namespaces once their source files have been bundled.
    ...['referralGiftAdmin', 'referralGiftPricing', 'referralGiftBanner', 'growthOffer', 'researchPilot'].filter(namespace => namespace in bundledPl)],
  defaultNS: 'common',
  supportedLngs: [...SUPPORTED_LANGUAGES],
  // Polish ships inside the JS bundle (generated by scripts/convert-locales.js),
  // so the default language renders instantly with no raw-key flash; the other
  // languages are still fetched over HTTP thanks to partialBundledLanguages.
  resources: { pl: bundledPl as Record<string, object> },
  partialBundledLanguages: true,
  backend: {
    loadPath: '/locales/{{lng}}/{{ns}}.yaml',
    parse: parseYaml,
  },
  lng: serverLanguage,
  fallbackLng: 'pl',
  react: {
    useSuspense: false,
  },
  interpolation: {
    escapeValue: false,
  },
});

/**
 * Server-rendered pages (/blog, /ustawa, ...) pick their language on the
 * server, where localStorage is invisible. Mirror the active language into
 * a cookie so getRequestLanguage() can honour the same preference instead
 * of falling back to Accept-Language - which is what made the homepage
 * render in Polish while /blog came back in English.
 */
export const LANGUAGE_COOKIE = 'preferredLanguage';

const persistLanguageCookie = (language?: string) => {
  if (typeof document === 'undefined' || !language) return;
  const base = language.split('-')[0];
  if (!SUPPORTED_LANGUAGES.includes(base as SupportedLanguageCode)) return;
  let source = 'automatic';
  try {
    if (localStorage.getItem('explicitLanguagePreference') === base) source = 'manual';
    else if (localStorage.getItem('accountLanguagePreference') === base) source = 'account';
    else if (localStorage.getItem('preferredLanguage') === base &&
      (localStorage.getItem('languagePreferenceSource') === 'manual' ||
        (!localStorage.getItem('languagePreferenceSource') && base !== 'pl'))) source = 'manual';
    localStorage.setItem('preferredLanguage', base);
    localStorage.setItem('languagePreferenceSource', source);
  } catch (_) { /* Storage may be unavailable. */ }
  document.cookie = `${LANGUAGE_COOKIE}=${base}; path=/; max-age=31536000; SameSite=Lax`;
  document.cookie = `languagePreferenceSource=${source}; path=/; max-age=31536000; SameSite=Lax`;
};

persistLanguageCookie(preferredLanguage);
i18next.on('languageChanged', persistLanguageCookie);

// Fetch the visitor's language while the page hydrates, so the switch after
// hydration is instant. Loading does not change the active language.
const preferredLanguageLoad: Promise<unknown> =
  preferredLanguage === serverLanguage
    ? Promise.resolve()
    : i18next.loadLanguages(preferredLanguage);

export interface LanguageSwitch {
  /** The language the UI is switching to: the visitor's own. */
  language: SupportedLanguageCode;
  /** Resolves with the active language once the switch is done or dropped. */
  settled: Promise<string>;
  /** Drops the switch if it has not happened yet. */
  cancel: () => void;
}

/**
 * Switch from the server's language to an explicit or account choice;
 * visitors without a choice and crawlers keep the domain default. Call it once the
 * page has hydrated (LanguageInitializer's effect).
 *
 * The switch never happens inside the caller's effect, even when the language
 * is already loaded: components subscribe to `languageChanged` in their own
 * effects, which run after LanguageInitializer's, and would miss it.
 *
 * A language picked by someone else in the meantime (the account's Firestore
 * preference in Auth, the settings page) wins: the switch is dropped.
 */
export const applyPreferredLanguage = (): LanguageSwitch => {
  if (!isBrowser) {
    return { language: serverLanguage, settled: Promise.resolve(serverLanguage), cancel: () => undefined };
  }
  let stale = false;
  const markStale = () => {
    stale = true;
  };
  const cancel = () => {
    stale = true;
    i18next.off('languageChanging', markStale);
  };
  i18next.on('languageChanging', markStale);
  const settled = preferredLanguageLoad.then(async () => {
    if (stale) return i18next.language;
    cancel();
    // Re-read: Auth may have stored the account's language since module load.
    const target = getInitialLanguage();
    if (target !== i18next.language) {
      await i18next.changeLanguage(target);
    }
    return i18next.language;
  });
  return { language: getInitialLanguage(), settled, cancel };
};

export default i18next;
